import { Button, Dialog, FilterToolbar, notify, SearchInput, Select, StatusPill, TextAreaField, TextField, type FilterItem, type PillTone } from "@flexfreight/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { shortDate } from "./CustomersList";

type Attribution = { source?: string; utmSource?: string; utmMedium?: string; utmCampaign?: string; landing?: string; referrer?: string } | null;
export type SignupRow = {
  id: string;
  name: string;
  createdAt: string;
  attribution: Attribution;
  ownerEmail: string | null;
  ownerName: string | null;
  firstQuoteAt: string | null;
  quotes: number;
  shipments: number;
  status: "pending" | "approved" | "hold" | "rejected";
  tier: string | null;
  repUserId: string | null;
  rep: string | null;
  note: string | null;
  reviewedAt: string | null;
};
export type StaffMember = { id: string; name: string | null; email: string };

const STATUS: Record<SignupRow["status"], { label: string; tone: PillTone }> = {
  pending: { label: "Needs review", tone: "warning" },
  approved: { label: "Approved", tone: "success" },
  hold: { label: "On hold", tone: "info" },
  rejected: { label: "Rejected", tone: "neutral" },
};
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/** Where the signup came from, in words: UTM source/campaign, else the referrer host, else not captured. */
export function sourceLabel(a: Attribution): string {
  if (!a) return "Not captured";
  if (a.utmSource) return [a.utmSource, a.utmMedium, a.utmCampaign].filter(Boolean).join(" / ");
  if (a.source) return a.source;
  if (a.referrer) {
    try {
      return new URL(a.referrer).hostname.replace(/^www\./, "");
    } catch {
      return a.referrer;
    }
  }
  return "Direct";
}

/** Customers > Signups & approvals: self-serve accounts to review, with source, first quote, tier and rep. */
export function SignupsPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "pending";
  const q = params.get("q") ?? "";
  const page = Number(params.get("page") ?? "0") || 0;
  const [search, setSearch] = useState(q);
  const set = (patch: Record<string, string | null>) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === "" || (k === "status" && v === "pending") || v === "0") n.delete(k);
          else n.set(k, v);
        }
        if (!("page" in patch)) n.delete("page");
        return n;
      },
      { replace: true },
    );
  useEffect(() => {
    const t = setTimeout(() => (search !== q ? set({ q: search.trim() }) : undefined), 250);
    return () => clearTimeout(t);
  }, [search]); // only the typed text drives the debounce
  const query = new URLSearchParams({ status, q, page: String(page) });
  const list = useQuery({
    queryKey: ["console", "signups", query.toString()],
    queryFn: () => api<{ signups: SignupRow[]; counts: { pending: number; hold: number }; hasMore: boolean }>(`/v1/console/signups?${query}`),
    placeholderData: keepPreviousData,
  });
  const [open, setOpen] = useState<SignupRow | null>(null);
  const items: FilterItem[] = [
    {
      key: "status",
      priority: 1,
      active: status !== "pending",
      inline: (
        <Select
          variant="toolbar"
          label="Status"
          value={status}
          onChange={(v) => set({ status: v })}
          options={[
            { value: "pending", label: `Needs review${list.data ? ` (${list.data.counts.pending})` : ""}` },
            { value: "hold", label: "On hold" },
            { value: "approved", label: "Approved" },
            { value: "rejected", label: "Rejected" },
            { value: "all", label: "All" },
          ]}
        />
      ),
    },
  ];
  const rows = list.data?.signups ?? [];
  const anyActive = status !== "pending" || q !== "";
  return (
    <section className="ffa-page">
      <header className="ffa-head">
        <div>
          <h1 className="ffa-page__title">Signups &amp; approvals</h1>
          <p className="ffa-head__sub">New self-serve accounts: where they came from, whether they quoted, and who owns them.</p>
        </div>
      </header>
      <p className="ffa-muted ffa-small">Review status is recorded for the team. It does not yet limit what the customer can do in the portal.</p>
      <FilterToolbar
        label="Signup filters"
        search={<SearchInput label="Search company or email" value={search} onChange={setSearch} />}
        items={items}
        anyActive={anyActive}
        onReset={() => {
          setSearch("");
          setParams(new URLSearchParams(), { replace: true });
        }}
      />
      {list.isError ? (
        <div className="ffa-notice" role="alert">
          <span>Couldn&apos;t load signups. {message(list.error)}</span>
          <Button size="sm" onClick={() => void list.refetch()}>Try again</Button>
        </div>
      ) : null}
      <div className="ffx-table-wrap ffa-tablewrap" aria-busy={list.isFetching}>
        <table className="ffx-table ffa-table">
          <caption className="ffx-sr">Signups</caption>
          <thead>
            <tr>
              <th scope="col">Company</th>
              <th scope="col">Signed up</th>
              <th scope="col">Source</th>
              <th scope="col">First quote</th>
              <th scope="col" className="ffx-table__num ffa-col-wide">Shipments</th>
              <th scope="col">Status</th>
              <th scope="col" className="ffa-col-wide">Rep</th>
              <th scope="col"><span className="ffx-sr">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {list.isPending ? (
              <tr><td colSpan={8} className="ffx-table__empty">Loading signups...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="ffx-table__empty">{anyActive ? "No signups match these filters." : "Nothing to review. New signups appear here."}</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td data-label="Company">
                    <Link className="ffa-cell-strong ffa-trunc ffa-rowlink" to={`/customers/${r.id}`} title={r.name}>{r.name}</Link>
                    <span className="ffa-muted ffa-block ffa-trunc">{r.ownerEmail ?? "No owner"}</span>
                  </td>
                  <td data-label="Signed up"><time dateTime={r.createdAt}>{shortDate(r.createdAt)}</time></td>
                  <td data-label="Source">{r.attribution ? sourceLabel(r.attribution) : <span className="ffa-muted">Not captured</span>}</td>
                  <td data-label="First quote">{r.firstQuoteAt ? <time dateTime={r.firstQuoteAt}>{shortDate(r.firstQuoteAt)}</time> : <span className="ffa-muted">None yet</span>}</td>
                  <td data-label="Shipments" className="ffx-table__num ffx-num ffa-col-wide">{r.shipments}</td>
                  <td data-label="Status">
                    <StatusPill tone={STATUS[r.status].tone}>{STATUS[r.status].label}</StatusPill>
                    {r.tier ? <span className="ffa-muted ffa-block">{r.tier}</span> : null}
                  </td>
                  <td data-label="Rep" className="ffa-col-wide">{r.rep ?? <span className="ffa-muted">Unassigned</span>}</td>
                  <td>
                    <Button size="sm" variant="ghost" onClick={() => setOpen(r)} aria-label={`Review ${r.name}`}>Review</Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <nav className="ffa-pager" aria-label="Pages">
        <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => set({ page: String(page - 1) })}>Previous</Button>
        <span className="ffa-muted">Page {page + 1}</span>
        <Button size="sm" variant="ghost" disabled={!list.data?.hasMore} onClick={() => set({ page: String(page + 1) })}>Next</Button>
      </nav>
      {open ? <ReviewDialog row={open} onClose={() => setOpen(null)} /> : null}
    </section>
  );
}

export function useStaff() {
  return useQuery({ queryKey: ["console", "staff"], queryFn: () => api<{ staff: StaffMember[] }>("/v1/console/staff"), staleTime: 300_000 });
}

function ReviewDialog({ row, onClose }: { row: SignupRow; onClose: () => void }) {
  const me = useConsoleMe();
  const qc = useQueryClient();
  const staff = useStaff();
  const [status, setStatus] = useState<SignupRow["status"]>(row.status);
  const [tier, setTier] = useState(row.tier ?? "");
  const [rep, setRep] = useState(row.repUserId ?? "");
  const [note, setNote] = useState(row.note ?? "");
  const canWrite = me.capabilities.includes("customers:write");
  const save = useMutation({
    mutationFn: () =>
      api(`/v1/console/signups/${row.id}`, { method: "PATCH", body: { status, tier: tier.trim() || null, repUserId: rep || null, note: note.trim() || null } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["console", "signups"] });
      notify.success("Signup updated", `${row.name}: ${STATUS[status].label}`);
      onClose();
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  const needsNote = (status === "hold" || status === "rejected") && note.trim() === "";
  const a = row.attribution;
  return (
    <Dialog
      open
      onClose={onClose}
      title={row.name}
      description={`${row.ownerName ?? row.ownerEmail ?? "No owner"} signed up ${shortDate(row.createdAt)}. ${row.quotes} quote${row.quotes === 1 ? "" : "s"}, ${row.shipments} shipment${row.shipments === 1 ? "" : "s"}.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {canWrite ? (
            <Button variant="primary" disabled={needsNote} loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
          ) : null}
        </>
      }
    >
      <div className="ffa-form">
        <dl className="ffa-dl">
          <dt>Source</dt>
          <dd>{sourceLabel(a)}</dd>
          {a?.landing ? (
            <>
              <dt>Landing page</dt>
              <dd>{a.landing}</dd>
            </>
          ) : null}
        </dl>
        {canWrite ? (
          <>
            <Select label="Decision" value={status} onChange={(v) => setStatus(v)} options={[{ value: "pending", label: "Needs review" }, { value: "approved", label: "Approve" }, { value: "hold", label: "Put on hold" }, { value: "rejected", label: "Reject" }]} />
            <TextField label="Tier" optional value={tier} onChange={(e) => setTier(e.target.value)} maxLength={40} hint="For example: Standard, Gold, Enterprise" />
            <Select label="Rep" value={rep} onChange={(v) => setRep(v)} options={[{ value: "", label: "Unassigned" }, ...(staff.data?.staff ?? []).map((s) => ({ value: s.id, label: s.name || s.email }))]} />
            <TextAreaField label="Note" optional={!(status === "hold" || status === "rejected")} value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={1000} error={needsNote ? "Say why, for the team" : undefined} />
          </>
        ) : (
          <p className="ffa-muted">Your role can view signups but not review them.</p>
        )}
      </div>
    </Dialog>
  );
}
